<!-- FortiGate traffic dashboard: template for FortinetDashboardComponent.
     Chart <div>s are containers ECharts draws into from the component;
     everything else is data-bound. Elements carrying data-ev are the
     targets of "Show" on an issue or a health check. -->
<!-- Own header: only without the application shell (navInShell=false).
     Inside DC Netscope the device picker lives in the vertical navigation
     and the lines below are drawn by the page toolbar instead. -->
<header class="app-header" *ngIf="!navInShell" [class.no-print]="!reportMode">
  <span class="title">FortiGate Traffic</span>
  <span class="tag picker">
    <label class="sr-only" for="serial-select">Device</label>
    <select id="serial-select" *ngIf="serials.length > 1"
            [value]="fleetMode ? fleetId : selectedSerial"
            (change)="setSerial($any($event.target).value)">
      <option [value]="fleetId" [selected]="fleetMode">All devices ({{ serials.length }})</option>
      <ng-container *ngIf="!pickerGroups.length">
        <option *ngFor="let device of pickerDevices; trackBy: trackBySerial" [value]="device.serial"
                [selected]="!fleetMode && device.serial === selectedSerial">{{ deviceText(device) }}</option>
      </ng-container>
      <optgroup *ngFor="let group of pickerGroups" [label]="group.label">
        <option *ngFor="let device of group.devices; trackBy: trackBySerial" [value]="device.serial"
                [selected]="!fleetMode && device.serial === selectedSerial">{{ deviceText(device) }}</option>
      </optgroup>
    </select>
    <input *ngIf="showPickerFilter" type="search" class="picker-filter"
           placeholder="Filter devices" aria-label="Filter the device list"
           [value]="pickerFilter" (input)="setPickerFilter($any($event.target).value)">
    <b *ngIf="serials.length <= 1">{{ deviceTitle }}<span class="tag-dim"
       *ngIf="deviceTitle !== serial"> {{ serial }}</span></b>
  </span>
  <span class="tag" *ngIf="!fleetMode">FortiOS <b>{{ version }}</b></span>
  <ng-container *ngTemplateOutlet="groupTags"></ng-container>
  <span class="label label-warning stale" *ngIf="!fleetMode && staleText"
        title="The collector has not polled this device recently; the figures are old">{{ staleText }}</span>
  <span id="status" role="status" [class.err]="statusErr">{{ statusText }}</span>
  <span class="hdr-actions">
    <button type="button" class="hdr-btn" (click)="refreshNow()">Refresh</button>
    <button type="button" class="hdr-btn" *ngIf="refreshMs > 0"
            [class.hdr-btn-on]="paused"
            [title]="paused ? 'Resume the automatic refresh' : 'Stop the automatic refresh, to read a chart without it moving'"
            (click)="togglePause()">{{ paused ? 'Resume' : 'Pause' }}</button>
    <button type="button" class="hdr-btn" *ngIf="!fleetMode" [disabled]="!issuesLoaded"
            [attr.aria-expanded]="shareOpen" (click)="shareOpen ? closeShare() : openShare()">Share summary</button>
    <button type="button" class="hdr-btn" *ngIf="!fleetMode" [disabled]="!issuesLoaded"
            (click)="printReport()">Print report</button>
  </span>
</header>

<main [hidden]="reportMode" [class.is-loading]="pendingRange" [attr.aria-busy]="!!pendingRange">
  <!-- a moving bar across the top of the page while a window loads -->
  <clr-progress-bar class="page-loading" *ngIf="pendingRange" clrLoop clrCompact></clr-progress-bar>
  <!-- Anonymised summary, for a ticket or an AI assistant -->
  <div class="card span12 share" *ngIf="shareOpen && !fleetMode">
    <div class="card-header">Share summary
      <span class="hdr-note">{{ shareReplaced }} identifying values replaced by placeholders</span>
    </div>
    <div class="card-block">
      <p class="share-note">Serial numbers, host names, IP and MAC addresses, admin
        names, policy and tunnel names are replaced before anything leaves the page.
        Review the text before sharing it.</p>
      <div class="share-leak" role="alert" *ngIf="shareLeaks.length">
        <span class="label label-danger">blocked</span>
        The summary still contains {{ shareLeaks.join(', ') }}, so copying is disabled.
      </div>
      <textarea readonly [id]="tabIds + '-share'" class="share-text" rows="14"
                aria-label="Anonymised summary" [value]="shareText"></textarea>
      <div class="share-actions">
        <button type="button" class="btn primary" [disabled]="shareLeaks.length > 0"
                (click)="copyShare()">Copy summary</button>
        <button type="button" class="btn" [disabled]="shareLeaks.length > 0"
                (click)="downloadShare()">Download .md</button>
        <button type="button" class="btn" (click)="closeShare()">Close</button>
        <span class="dim" role="status">{{ shareStatus }}</span>
      </div>
    </div>
  </div>

  <!-- Page toolbar: what the device page says about itself, plus the
       history window. With the shell (navInShell) it also carries the
       facts and the actions the component's own header used to show. -->
  <div class="card span12" id="hist-toolbar">
    <div class="toolbar page-toolbar" *ngIf="navInShell">
      <span class="page-title">{{ fleetMode ? 'All devices' : tabLabel(tab) }}</span>
      <span class="tag" *ngIf="!fleetMode">FortiOS <b>{{ version }}</b></span>
      <ng-container *ngTemplateOutlet="groupTags"></ng-container>
      <span class="tag" *ngIf="!fleetMode && serial && serial !== '-' && serial !== deviceTitle">{{ serial }}</span>
      <span class="label label-warning" *ngIf="!fleetMode && staleText"
            title="The collector has not polled this device recently; the figures are old">{{ staleText }}</span>
      <span class="tb-status" role="status" [class.err]="statusErr">{{ statusText }}</span>
      <span class="hdr-actions">
        <button type="button" class="btn" (click)="refreshNow()">Refresh</button>
        <!-- No aria-pressed here: the label itself changes (Pause / Resume),
             and the ARIA Authoring Practices say a toggle button keeps the
             same label ("Resume, pressed" would mean the opposite). -->
        <button type="button" class="btn" *ngIf="refreshMs > 0"
                [class.primary]="paused"
                [title]="paused ? 'Resume the automatic refresh' : 'Stop the automatic refresh, to read a chart without it moving'"
                (click)="togglePause()">{{ paused ? 'Resume' : 'Pause' }}</button>
        <button type="button" class="btn" *ngIf="!fleetMode" [disabled]="!issuesLoaded"
                [attr.aria-expanded]="shareOpen" (click)="shareOpen ? closeShare() : openShare()">Share summary</button>
        <button type="button" class="btn" *ngIf="!fleetMode" [disabled]="!issuesLoaded"
                (click)="printReport()">Print report</button>
      </span>
    </div>
    <div class="toolbar">
      <span class="tb-title">History window</span>
      <span class="btn-group" role="group" aria-label="History window">
        <button *ngFor="let range of ranges; trackBy: trackByRangeId"
                type="button"
                [class.active]="range.id === histRange"
                [class.loading]="range.id === pendingRange"
                [attr.aria-pressed]="range.id === histRange"
                [attr.aria-busy]="range.id === pendingRange"
                (click)="setRange(range.id)"><clr-spinner *ngIf="range.id === pendingRange"
                clrInline clrSmall>Loading</clr-spinner>{{ range.label }}</button>
      </span>
      <!-- What happens after a click on a window: loading (with the seconds,
           since a long window can take a while to build), then a short
           confirmation, then which sessions the page was built from.
           aria-live: a screen reader says it too. -->
      <span class="range-state" aria-live="polite">
        <ng-container *ngIf="pendingRange">
          Loading {{ rangeLabel(pendingRange) }}…
          <span *ngIf="pendingSeconds > 0">{{ pendingSeconds }} s</span>
          <span class="dim" *ngIf="pendingSeconds >= 3"> (a long window takes longer to build)</span>
        </ng-container>
        <span class="range-done" *ngIf="!pendingRange && rangeDoneText">✓ {{ rangeDoneText }}</span>
        <span *ngIf="!pendingRange && !rangeDoneText && coverageText" [class.range-cut]="coverageCut"
              [title]="coverageCut ? 'Too many sessions to read the whole window: the oldest polls were left out. The traffic breakdown and the network map start later than the window.' : 'The traffic breakdown and the network map are built from these session lists'">
          {{ coverageText }}<span *ngIf="coverageCut"> (window cut short)</span>
        </span>
      </span>
      <span class="hdr-note">Ctrl + scroll or the zoom tool on a chart zooms every
        time chart together<span *ngIf="bucketed">. Each point averages several
        polls; the shaded band spans their min and max</span></span>
    </div>
  </div>

  <!-- All-devices overview: replaces the device page -->
  <app-fortinet-fleet *ngIf="fleetMode" class="span12"
                      [range]="histRange" [refreshMs]="refreshMs" [paused]="paused"
                      [apiBase]="apiBase"
                      [initialGroupFilter]="pendingFleetGroup"
                      (openDevice)="setSerial($event)"
                      (openIssue)="onFleetIssue($event)"
                      (loaded)="onFleetLoaded($event)"
                      (status)="onFleetStatus($event)"
                      (filtersChanged)="onFleetFilters($event)"></app-fortinet-fleet>

  <ng-container *ngIf="!fleetMode">
  <!-- Device page tabs. With the application shell they are entries of the
       vertical navigation instead (see fortinet-shared.FortinetNavService). -->
  <nav class="span12 page-tabs" role="tablist" aria-label="Device page"
       *ngIf="!navInShell" (keydown)="onTabKey($event)">
    <button *ngFor="let tabEntry of tabs; trackBy: trackByTab" type="button" role="tab"
            class="page-tab" [id]="tabIds + '-' + tabEntry.id"
            [attr.aria-controls]="tabIds + '-panel'"
            [attr.aria-selected]="tab === tabEntry.id"
            [attr.aria-disabled]="tabOffReason(tabEntry.id) ? true : null"
            [class.off]="!!tabOffReason(tabEntry.id)"
            [title]="tabOffReason(tabEntry.id)"
            [tabIndex]="tab === tabEntry.id ? 0 : -1"
            (click)="setTab(tabEntry.id)">{{ tabEntry.label }}<span class="tab-count"
              *ngIf="tabEntry.id === 'overview' && issues.length">{{ issues.length }}</span></button>
  </nav>

  <div class="tab-panel span12" [attr.role]="navInShell ? null : 'tabpanel'"
       [id]="tabIds + '-panel'"
       [attr.aria-labelledby]="navInShell ? null : tabIds + '-' + tab"
       [attr.aria-label]="navInShell ? tabLabel(tab) : null">

  <!-- ================================================================ OVERVIEW -->
  <ng-container *ngIf="tab === 'overview'">
    <div class="kpi-row span12">
      <button type="button" class="card kpi" *ngFor="let kpi of kpis; trackBy: trackByKpi"
              [attr.aria-label]="kpi.label + ': ' + kpi.value + (kpi.suffix || '') + '. Open the ' + tabLabel(kpi.tab) + ' tab'"
              (click)="kpi.tab && setTab(kpi.tab)">
        <span class="card-block">
          <span class="kpi-label">{{ kpi.label }}</span>
          <span class="kpi-value">
            {{ kpi.value }}<span class="trend kpi-suffix" *ngIf="kpi.suffix">{{ kpi.suffix }}</span>
            <span *ngIf="kpi.trend" class="trend" [ngClass]="kpi.trend.cls"
                  [title]="kpi.trend.title">{{ kpi.trend.arrow }} {{ kpi.trend.pct }}%</span>
          </span>
          <span class="kpi-sub">
            <span *ngIf="kpi.badge" class="label" [ngClass]="kpi.badge.cls">{{ kpi.badge.text }}</span>
            <ng-container *ngIf="!kpi.badge">{{ kpi.sub }}</ng-container>
          </span>
        </span>
      </button>
    </div>

    <!-- Health checks: every check the data allows, passing ones included -->
    <div class="card span12" data-ev="card:checks">
      <div class="card-header">Health checks
        <span class="hdr-note">{{ checksSummary }}</span>
      </div>
      <div class="checks">
        <div class="empty" *ngIf="!checkGroups.length">Collecting…</div>
        <section class="check-group" *ngFor="let checkGroup of checkGroups">
          <h3 class="check-group-title">{{ checkGroup.name }}</h3>
          <ul class="check-list">
            <li *ngFor="let check of checkGroup.checks; trackBy: trackByCheck" [attr.data-ev]="'check:' + check.id">
              <button type="button" class="check" [title]="check.title"
                      [disabled]="!check.evidence"
                      (click)="check.evidence && showEvidence(check.evidence)">
                <span class="label check-pill" [ngClass]="check.badge.cls">{{ check.badge.text }}</span>
                <span class="check-label">{{ check.label }}</span>
                <span class="check-metric">{{ check.metric }}</span>
              </button>
            </li>
          </ul>
        </section>
      </div>
    </div>

    <div class="card" [ngClass]="hasHealth ? 'span6' : 'span12'">
      <div class="card-header has-tabs">
        <span class="card-title">Issues to solve</span>
        <div class="tabs" role="tablist" *ngIf="ackSupported"
             (keydown)="onIssuesTabKey($event, tabOpen, tabAcked)">
          <button #tabOpen type="button" class="tab" role="tab"
                  [id]="tabIdPrefix + '-open'" [attr.aria-controls]="tabIdPrefix + '-panel'"
                  [attr.aria-selected]="issuesTab === 'open'"
                  [tabIndex]="issuesTab === 'open' ? 0 : -1"
                  (click)="selectIssuesTab('open')">Open
            <span class="tab-count">{{ issues.length }}</span></button>
          <button #tabAcked type="button" class="tab" role="tab"
                  [id]="tabIdPrefix + '-acked'" [attr.aria-controls]="tabIdPrefix + '-panel'"
                  [attr.aria-selected]="issuesTab === 'acked'"
                  [tabIndex]="issuesTab === 'acked' ? 0 : -1"
                  (click)="selectIssuesTab('acked')">Acknowledged
            <span class="tab-count">{{ issuesAcked.length }}</span></button>
        </div>
      </div>
      <div class="issues issues-tall" [attr.role]="ackSupported ? 'tabpanel' : null"
           [id]="tabIdPrefix + '-panel'"
           [attr.aria-labelledby]="ackSupported ? tabIdPrefix + '-' + issuesTab : null">
        <div class="empty" *ngIf="!issuesLoaded">Collecting…</div>
        <ng-container *ngIf="issuesLoaded && issuesTab === 'open'">
        <div class="ok" *ngIf="!issues.length">
          <span class="label label-success">{{ issuesAcked.length
            ? 'No open issues' : 'No issues detected' }}</span>
        </div>
        <div class="issue" *ngFor="let issue of issues; trackBy: trackByIssue">
          <label class="ack-box" *ngIf="ackSupported && issue.id"
                 title="Acknowledge: move it to the Acknowledged tab">
            <input type="checkbox" [checked]="false"
                   [disabled]="ackPending.has(issue.id)"
                   [attr.aria-label]="'Acknowledge: ' + issue.title"
                   (change)="setAck(issue, true)">
          </label>
          <span class="label" [ngClass]="(issue | issueBadge).cls">{{ (issue | issueBadge).text }}</span>
          <span class="issue-body">
            <span class="issue-title">{{ issue.title }}</span>
            <button type="button" class="link-btn ev-link" *ngIf="hasEvidence(issue)"
                    [attr.aria-label]="'Show the data behind: ' + issue.title"
                    (click)="goToEvidence(issue.id)">Show</button>
            <ng-container *ngIf="issue.detail">
              <br><span class="issue-detail">{{ issue.detail }}</span>
            </ng-container>
            <ng-container *ngIf="firstSeenText(issue) as since">
              <br><span class="issue-since" [title]="firstSeenTitle(issue)">{{ since }}</span>
            </ng-container>
            <ng-container *ngIf="ackNote(issue, true) as note">
              <br><span class="ack-note ack-note-worse">{{ note }}<ng-container
                *ngIf="ackNoteLine(issue) as line"> {{ line }}</ng-container></span>
            </ng-container>
          </span>
        </div>
        </ng-container>
        <ng-container *ngIf="issuesLoaded && issuesTab === 'acked'">
          <div class="empty" *ngIf="!issuesAcked.length">Nothing acknowledged.
            Tick an issue in Open to move it here.</div>
          <div class="issue" *ngFor="let issue of issuesAcked; trackBy: trackByIssue">
            <label class="ack-box" title="Move it back to the Open tab">
              <input type="checkbox" [checked]="true"
                     [disabled]="ackPending.has(issue.id ?? '')"
                     [attr.aria-label]="'Remove acknowledgement: ' + issue.title"
                     (change)="setAck(issue, false)">
            </label>
            <span class="label" [ngClass]="(issue | issueBadge).cls">{{ (issue | issueBadge).text }}</span>
            <span class="issue-body">
              <span class="issue-title">{{ issue.title }}</span>
              <button type="button" class="link-btn ev-link" *ngIf="issue.id && noteEd.key !== issue.id"
                      [attr.aria-label]="(issue.ack_note ? 'Edit the note of: ' : 'Add a note to: ') + issue.title"
                      (click)="openNote(issue)">{{ issue.ack_note ? 'Edit note' : 'Note' }}</button>
              <br><span class="ack-note">{{ ackNote(issue, false) }}</span>
              <ng-container *ngIf="ackNoteLine(issue) as line">
                <br><span class="ack-note-text" [title]="issue.ack_note_at ? 'Written ' + fmtStamp(issue.ack_note_at) : ''">{{ line }}</span>
              </ng-container>
              <form class="note-form" *ngIf="noteEd.key === issue.id" (submit)="$event.preventDefault(); saveNote(issue)">
                <label class="sr-only" [for]="tabIdPrefix + '-note'">Why is it acknowledged?</label>
                <input [id]="tabIdPrefix + '-note'" class="note-input" maxlength="500"
                       placeholder="Why (e.g. spare port, planned change)"
                       [value]="noteEd.note" (input)="noteEd.note = $any($event.target).value">
                <label class="sr-only" [for]="tabIdPrefix + '-by'">Your name</label>
                <input [id]="tabIdPrefix + '-by'" class="note-by" maxlength="80" placeholder="Your name"
                       [readOnly]="noteEd.byFixed"
                       [value]="noteEd.by" (input)="noteEd.by = $any($event.target).value">
                <button type="submit" class="btn primary" [disabled]="noteEd.saving">Save</button>
                <button type="button" class="btn" (click)="cancelNote()">Cancel</button>
                <span class="note-err" role="alert" *ngIf="noteEd.error">{{ noteEd.error }}</span>
              </form>
            </span>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Firmware + licenses: a status strip on top, the licenses below -->
    <div class="card span6" *ngIf="hasHealth" data-ev="card:licenses">
      <div class="card-header">Licenses &amp; firmware
        <span class="hdr-note">{{ licenseActive.length }} active<span
          *ngIf="licenseInactive.length">, {{ licenseInactive.length }} inactive</span></span>
      </div>
      <div class="fw-strip">
        <div>
          <span class="fw-label">FortiOS</span>
          <span class="fw-version">{{ version }}</span>
          <span class="fw-target dim" *ngIf="deviceInfo?.firmware_target">target {{ deviceInfo!.firmware_target }}</span>
        </div>
        <span class="fw-badges">
          <span class="label label-success" *ngIf="deviceInfo?.firmware_status === 'on-target'">on target</span>
          <span class="label label-warning" *ngIf="deviceInfo?.firmware_status === 'behind'">behind target</span>
          <span class="label label-info" *ngIf="deviceInfo?.firmware_status === 'ahead'">ahead of target</span>
          <span class="label"
                [ngClass]="firmware?.next?.length ? 'label-info' : 'label-success'">
            {{ firmware?.next?.length
               ? 'upgrade to ' + (firmware!.latest || firmware!.next[firmware!.next.length - 1])
               : 'up to date' }}
          </span>
        </span>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!licenseActive.length && !licenseInactive.length">
          No license data.
        </div>
        <table class="clr license-tbl sortable" *ngIf="licenseActive.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="licenseSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(licenseSort, 'name')">License <span class="sort-mark" aria-hidden="true">{{ licenseSort.mark('name') }}</span></button></th>
              <th class="num" [attr.aria-sort]="licenseSort.ariaSort('status')">
                <button type="button" class="sort-btn" (click)="sortTable(licenseSort, 'status')">Status <span class="sort-mark" aria-hidden="true">{{ licenseSort.mark('status') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let license of licenseActiveShown; trackBy: trackByName" [attr.data-ev]="'license:' + license.name">
              <td><b>{{ license.name }}</b></td>
              <td class="num">
                <span class="label" [ngClass]="licenseBadge(license).cls">{{ licenseBadge(license).text }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="lic-more" *ngIf="licenseInactive.length">
          <button type="button" class="link-btn" [attr.aria-expanded]="showInactiveLicenses"
                  (click)="toggleInactiveLicenses()">
            {{ showInactiveLicenses ? '▾ Hide' : '▸ Show' }}
            {{ licenseInactive.length }} unlicensed feature(s)
          </button>
          <table class="clr license-tbl" *ngIf="showInactiveLicenses">
            <tbody>
              <tr *ngFor="let license of licenseInactiveShown; trackBy: trackByName" [attr.data-ev]="'license:' + license.name">
                <td class="dim">{{ license.name }}</td>
                <td class="num"><span class="label label-info">{{ license.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <!-- State changes between polls: links, tunnels, peers, failovers,
         conserve mode, reboots, firmware, admin sessions -->
    <div class="card span12" *ngIf="events.length || eventsTotal" data-ev="card:events">
      <div class="card-header has-tabs">
        <span class="card-title">Events</span>
        <span class="chips" role="group" aria-label="Event kind">
          <button type="button" class="chip" [attr.aria-pressed]="eventKind === 'all'"
                  (click)="setEventKind('all')">All <span class="tab-count">{{ events.length }}</span></button>
          <button type="button" class="chip" *ngFor="let kind of eventKinds; trackBy: trackByKind"
                  [attr.aria-pressed]="eventKind === kind"
                  (click)="setEventKind(kind)">{{ eventKindLabel[kind] }}</button>
        </span>
        <span class="hdr-note">changes between two polls; hover a dot to see it on
          the other charts<span *ngIf="eventsTotal > events.length">; newest
          {{ events.length }} of {{ eventsTotal }}</span></span>
      </div>
      <div class="card-block"><div id="ch-events" class="chart chart-events"
                                   [style.height.px]="eventsChartHeight"></div></div>
      <div class="events-list">
        <div class="event" *ngFor="let event of eventsShown; trackBy: trackByEvent">
          <span class="event-time" [title]="eventWhen(event)">{{ fmtStamp(event.t) }}</span>
          <span class="label" [ngClass]="eventBadge(event.sev).cls">{{ eventBadge(event.sev).text }}</span>
          <span class="event-title">{{ event.title }}</span>
          <button type="button" class="link-btn ev-link" *ngIf="eventHasEvidence(event)"
                  [attr.aria-label]="'Show the data behind: ' + event.title"
                  (click)="goToEvidence(event.ref ?? undefined)">Show</button>
        </div>
      </div>
      <div class="lic-more" *ngIf="eventsMatching > eventRowsCollapsed">
        <button type="button" class="link-btn" [attr.aria-expanded]="showAllEvents"
                (click)="toggleAllEvents()">
          {{ showAllEvents ? '▾ Show the newest ' + eventRowsCollapsed + ' only'
            : '▸ Show all ' + eventsMatching + ' events' }}
        </button>
      </div>
    </div>

    <div class="card span12">
      <div class="card-header">Traffic per day
        <span class="hdr-note">sum of interface counters, full history; transit
          traffic counts on both its interfaces</span>
      </div>
      <div class="card-block"><div id="ch-daily" class="chart chart-daily"></div></div>
    </div>
  </ng-container>

  <!-- ================================================================ TRAFFIC -->
  <ng-container *ngIf="tab === 'traffic'">
    <!-- The interface table gets one more grid column when it shows the
         Duplex column, otherwise it doesn't fit in its card -->
    <div class="card" [ngClass]="ifaceHasHalfDuplex ? 'span7' : 'span8'" data-ev="card:iface-chart">
      <div class="card-header">Interface throughput
        <span class="hdr-note">RX solid, TX dashed</span>
      </div>
      <div class="card-block"><div id="ch-iface" class="chart tall"></div></div>
    </div>
    <div class="card" [ngClass]="ifaceHasHalfDuplex ? 'span5' : 'span4'" data-ev="card:interfaces">
      <div class="card-header has-filter">
        <span class="card-title">Interfaces</span>
        <input type="search" class="card-filter" placeholder="Filter"
               aria-label="Filter interfaces by name, alias or address"
               [value]="ifaceFilter" (input)="setIfaceFilter($any($event.target).value)">
        <button type="button" class="link-btn" [disabled]="!ifaceShown.length"
                title="Download the table as shown (filter and order included)"
                (click)="exportIfacesCsv()">CSV</button>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!ifaceRows.length">No interface data yet.</div>
        <div class="empty" *ngIf="ifaceRows.length && !ifaceShown.length">No interface matches.</div>
        <table class="clr sortable" *ngIf="ifaceShown.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="ifaceSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(ifaceSort, 'name')">Interface <span class="sort-mark" aria-hidden="true">{{ ifaceSort.mark('name') }}</span></button></th>
              <th class="pill-col" [attr.aria-sort]="ifaceSort.ariaSort('link')">
                <button type="button" class="sort-btn" (click)="sortTable(ifaceSort, 'link')">Link <span class="sort-mark" aria-hidden="true">{{ ifaceSort.mark('link') }}</span></button></th>
              <th class="pill-col" *ngIf="ifaceHasHalfDuplex" [attr.aria-sort]="ifaceSort.ariaSort('duplex')">
                <button type="button" class="sort-btn" (click)="sortTable(ifaceSort, 'duplex')">Duplex <span class="sort-mark" aria-hidden="true">{{ ifaceSort.mark('duplex') }}</span></button></th>
              <th class="num" [attr.aria-sort]="ifaceSort.ariaSort('rx')">
                <button type="button" class="sort-btn" (click)="sortTable(ifaceSort, 'rx')">RX now <span class="sort-mark" aria-hidden="true">{{ ifaceSort.mark('rx') }}</span></button></th>
              <th class="num" [attr.aria-sort]="ifaceSort.ariaSort('tx')">
                <button type="button" class="sort-btn" (click)="sortTable(ifaceSort, 'tx')">TX now <span class="sort-mark" aria-hidden="true">{{ ifaceSort.mark('tx') }}</span></button></th>
              <th class="num" [attr.aria-sort]="ifaceSort.ariaSort('util')">
                <button type="button" class="sort-btn" (click)="sortTable(ifaceSort, 'util')">Util <span class="sort-mark" aria-hidden="true">{{ ifaceSort.mark('util') }}</span></button></th>
              <th class="num" [attr.aria-sort]="ifaceSort.ariaSort('errors')">
                <button type="button" class="sort-btn" (click)="sortTable(ifaceSort, 'errors')">Errors <span class="sort-mark" aria-hidden="true">{{ ifaceSort.mark('errors') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let iface of ifaceShown; trackBy: trackByName" [attr.data-ev]="'iface:' + iface.name">
              <td>
                <b>{{ iface.name }}</b>
                <span class="dim" *ngIf="iface.alias"> {{ iface.alias }}</span>
              </td>
              <td class="pill-col">
                <span class="label" [ngClass]="iface.link ? 'label-success' : 'label-danger'">
                  {{ iface.link ? 'up' : 'down' }}
                </span>
              </td>
              <!-- its own column, so the pills line up (see ifaceHasHalfDuplex) -->
              <td class="pill-col" *ngIf="ifaceHasHalfDuplex">
                <!-- a port without link often says "half": only shown on a link that is up -->
                <span class="label label-warning" *ngIf="iface.link && iface.duplex === 0">half</span>
                <span class="dim" *ngIf="iface.link && iface.duplex === 1">full</span>
                <span class="dim" *ngIf="!iface.link || iface.duplex == null">-</span>
              </td>
              <td class="num">{{ iface.rx_bps | fmtBps }}</td>
              <td class="num">{{ iface.tx_bps | fmtBps }}</td>
              <td class="num util-cell" [ngClass]="utilClass(iface.util)">
                {{ iface.util == null ? '-' : iface.util + '%' }}
                <span class="util-bar" *ngIf="iface.util != null" aria-hidden="true"><span
                  [ngClass]="utilClass(iface.util)" [style.width.%]="utilWidth(iface.util)"></span></span>
              </td>
              <td class="num" [title]="ifaceErr[iface.name]?.title || 'Lifetime counter'"
                  [ngClass]="ifaceErr[iface.name]?.cls || 'dim'">
                {{ iface.rx_errors + iface.tx_errors | fmtNum }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card" [ngClass]="hasIfaceErrors ? 'span6' : 'span12'">
      <div class="card-header">Active sessions &amp; setup rate</div>
      <div class="card-block"><div id="ch-sessions" class="chart"></div></div>
    </div>
    <div class="card span6" *ngIf="hasIfaceErrors" data-ev="card:iface-errors">
      <div class="card-header">Interface errors
        <span class="hdr-note">errors/s from rx+tx error counters</span>
      </div>
      <div class="card-block"><div id="ch-iface-err" class="chart"></div></div>
    </div>

    <!-- Traffic breakdown: overview first (flow composition), then who
         (hosts), then what (services, ports), then the network map -->
    <div class="card span8">
      <div class="card-header">Internal vs external traffic
        <span class="hdr-note">source, service and destination, by bytes</span>
      </div>
      <div class="card-block"><div id="ch-scope" class="chart"></div></div>
    </div>
    <div class="card span4">
      <div class="card-header">Transport protocol mix</div>
      <div class="card-block"><div id="ch-proto" class="chart"></div></div>
    </div>
    <div class="card span6">
      <div class="card-header">Top senders
        <span class="hdr-note">hosts by bytes sent, whichever side opened
          the session</span>
      </div>
      <div class="card-block"><div id="ch-senders" class="chart"></div></div>
    </div>
    <div class="card span6">
      <div class="card-header">Top receivers
        <span class="hdr-note">hosts by bytes received, whichever side opened
          the session</span>
      </div>
      <div class="card-block"><div id="ch-receivers" class="chart"></div></div>
    </div>
    <div class="card span6">
      <div class="card-header">Services</div>
      <div class="card-block"><div id="ch-services" class="chart"></div></div>
    </div>
    <div class="card span6">
      <div class="card-header">Top destination ports
        <label class="toggle">
          <span class="toggle-caption"
                [class.dim-caption]="dportsByVolume">sessions</span>
          <!-- role="switch": the APG switch pattern, on a real checkbox -->
          <input type="checkbox" role="switch"
                 [checked]="dportsByVolume"
                 aria-label="Rank ports by volume instead of sessions"
                 (change)="setDportsByVolume($any($event.target).checked)">
          <span class="toggle-track"></span>
          <span class="toggle-caption"
                [class.dim-caption]="!dportsByVolume">volume</span>
        </label>
      </div>
      <div class="card-block"><div id="ch-dports" class="chart"></div></div>
    </div>
    <!-- Top applications: disabled until the FortiGate reports per-session
         application data (needs an Application Control profile on the
         policies, or a FortiView-based collector). To restore: uncomment
         this card and set it and the two cards above to span4.
    <div class="card span4">
      <div class="card-header">Top applications</div>
      <div class="card-block"><div id="ch-apps" class="chart"></div></div>
    </div>
    -->
  </ng-container>

  <!-- ================================================================ POLICIES -->
  <ng-container *ngIf="tab === 'policies'">
    <div class="card span12">
      <div class="toolbar">
        <label class="search">
          <span class="sr-only">Find a policy</span>
          <input type="search" placeholder="Find a policy by id or name"
                 [value]="policyFilter" (input)="setPolicyFilter($any($event.target).value)">
        </label>
        <span class="hdr-note" *ngIf="policyFilter">{{ policyUsedMatches }} used and
          {{ policyLessUsedMatches }} less used policies match</span>
        <button type="button" class="btn" [class.push-right]="!policyFilter"
                [disabled]="!policyUsedRows.length && !policyLessUsedRows.length"
                title="Every policy of the device with its counters, busiest first"
                (click)="exportPoliciesCsv()">Export CSV</button>
      </div>
    </div>
    <div class="card span6">
      <div class="card-header">Per-policy throughput</div>
      <div class="card-block"><div id="ch-throughput" class="chart"></div></div>
    </div>
    <div class="card span6">
      <div class="card-header">Active sessions per policy</div>
      <div class="card-block"><div id="ch-polsess" class="chart"></div></div>
    </div>

    <div class="card span6" data-ev="card:top-used">
      <div class="card-header">Top used policies
        <span class="hdr-note" *ngIf="policyUsedSort.key === 'now' && !policyUsedSort.ascending">sorted by current throughput</span>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!policyUsedShown.length">{{ policyFilter
          ? 'No used policy matches.' : 'No active policies yet.' }}</div>
        <table class="clr sortable" *ngIf="policyUsedShown.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="policyUsedSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(policyUsedSort, 'name')">Policy <span class="sort-mark" aria-hidden="true">{{ policyUsedSort.mark('name') }}</span></button></th>
              <th class="num" [attr.aria-sort]="policyUsedSort.ariaSort('now')">
                <button type="button" class="sort-btn" (click)="sortTable(policyUsedSort, 'now')">Now <span class="sort-mark" aria-hidden="true">{{ policyUsedSort.mark('now') }}</span></button></th>
              <th class="num" [attr.aria-sort]="policyUsedSort.ariaSort('sessions')">
                <button type="button" class="sort-btn" (click)="sortTable(policyUsedSort, 'sessions')">Sessions <span class="sort-mark" aria-hidden="true">{{ policyUsedSort.mark('sessions') }}</span></button></th>
              <th class="num" [attr.aria-sort]="policyUsedSort.ariaSort('hits')">
                <button type="button" class="sort-btn" (click)="sortTable(policyUsedSort, 'hits')">Hits <span class="sort-mark" aria-hidden="true">{{ policyUsedSort.mark('hits') }}</span></button></th>
              <th class="num" [attr.aria-sort]="policyUsedSort.ariaSort('total')">
                <button type="button" class="sort-btn" (click)="sortTable(policyUsedSort, 'total')">Total <span class="sort-mark" aria-hidden="true">{{ policyUsedSort.mark('total') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let policy of policyUsedShown; trackBy: trackByName" [attr.data-ev]="'policy:' + policy.name">
              <td><b>{{ policy.name }}</b>
                <span class="label label-warning" *ngIf="oversizeNames.has(policy.name)"
                      title="Too many addresses to offload to the hardware">oversize</span></td>
              <td class="num">{{ policy.bps_now | fmtBps }}</td>
              <td class="num">{{ policy.active_sessions | fmtNum }}</td>
              <td class="num">{{ policy.hits | fmtNum }}</td>
              <td class="num dim">{{ policy.bytes | fmtBytes }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="lic-more" *ngIf="!policyFilter && policyUsedRows.length > policyRowsCollapsed">
        <button type="button" class="link-btn" [attr.aria-expanded]="showAllUsedPolicies"
                (click)="toggleUsedPolicies()">
          {{ showAllUsedPolicies
            ? '▾ Show the top ' + policyRowsCollapsed + ' only'
            : '▸ Show all ' + policyUsedRows.length + ' policies' }}
        </button>
      </div>
    </div>

    <!-- Less used: the unused (zero-hit) policies when there are some,
         otherwise the policies with the fewest hits -->
    <div class="card span6" data-ev="card:less-used">
      <div class="card-header">Less used policies
        <span class="hdr-note">{{ lessUsedShowsUnused ? 'zero hits recorded' : 'fewest hits recorded' }}<ng-container
          *ngIf="!policyLessUsedSort.key">{{ lessUsedShowsUnused ? ', sorted by name' : ', sorted by hits' }}</ng-container></span>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!policyLessUsedShown.length">{{ policyFilter
          ? 'No policy matches.' : 'No policies yet.' }}</div>
        <table class="clr sortable" *ngIf="policyLessUsedShown.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="policyLessUsedSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(policyLessUsedSort, 'name')">Policy <span class="sort-mark" aria-hidden="true">{{ policyLessUsedSort.mark('name') }}</span></button></th>
              <th class="num" [attr.aria-sort]="policyLessUsedSort.ariaSort('hits')">
                <button type="button" class="sort-btn" (click)="sortTable(policyLessUsedSort, 'hits')">Hits <span class="sort-mark" aria-hidden="true">{{ policyLessUsedSort.mark('hits') }}</span></button></th>
              <th class="num" [attr.aria-sort]="policyLessUsedSort.ariaSort('sessions')">
                <button type="button" class="sort-btn" (click)="sortTable(policyLessUsedSort, 'sessions')">Sessions <span class="sort-mark" aria-hidden="true">{{ policyLessUsedSort.mark('sessions') }}</span></button></th>
              <th class="num" [attr.aria-sort]="policyLessUsedSort.ariaSort('total')">
                <button type="button" class="sort-btn" (click)="sortTable(policyLessUsedSort, 'total')">Total <span class="sort-mark" aria-hidden="true">{{ policyLessUsedSort.mark('total') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let policy of policyLessUsedShown; trackBy: trackByName" [attr.data-ev]="'policy:' + policy.name">
              <td><b>{{ policy.name }}</b>
                <span class="label label-warning" *ngIf="oversizeNames.has(policy.name)">oversize</span></td>
              <td class="num dim">{{ policy.hits | fmtNum }}</td>
              <td class="num dim">{{ policy.active_sessions | fmtNum }}</td>
              <td class="num dim">{{ policy.bytes | fmtBytes }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="lic-more" *ngIf="!policyFilter && policyLessUsedRows.length > policyRowsCollapsed">
        <button type="button" class="link-btn" [attr.aria-expanded]="showAllLessUsedPolicies"
                (click)="toggleLessUsedPolicies()">
          {{ showAllLessUsedPolicies
            ? '▾ Show the first ' + policyRowsCollapsed + ' only'
            : '▸ Show all ' + policyLessUsedRows.length + ' policies' }}
        </button>
      </div>
    </div>
  </ng-container>

  <!-- ================================================================ VPN & WAN -->
  <ng-container *ngIf="tab === 'vpn'">
    <div class="card span12" *ngIf="!hasWanVpn">
      <div class="empty">This device reports no SD-WAN health checks, IPsec tunnels
        or SSL-VPN users in this window.</div>
    </div>
    <div class="card" [ngClass]="sdwanRows.length ? 'span8' : 'span12'" *ngIf="hasSdwan">
      <div class="card-header">SD-WAN link health
        <span class="hdr-note">latency, jitter and loss per health-check member</span>
      </div>
      <div class="card-block"><div id="ch-sdwan" class="chart chart-sdwan"></div></div>
    </div>
    <div class="card" [ngClass]="hasSdwan ? 'span4' : 'span12'" *ngIf="sdwanRows.length"
         data-ev="card:sdwan-members">
      <div class="card-header">SD-WAN members
        <span class="hdr-note">latest health-check result</span>
      </div>
      <div class="tbl-wrap">
        <table class="clr sortable">
          <thead>
            <tr>
              <th [attr.aria-sort]="sdwanSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(sdwanSort, 'name')">Member <span class="sort-mark" aria-hidden="true">{{ sdwanSort.mark('name') }}</span></button></th>
              <th [attr.aria-sort]="sdwanSort.ariaSort('state')">
                <button type="button" class="sort-btn" (click)="sortTable(sdwanSort, 'state')">State <span class="sort-mark" aria-hidden="true">{{ sdwanSort.mark('state') }}</span></button></th>
              <th class="num" [attr.aria-sort]="sdwanSort.ariaSort('latency')">
                <button type="button" class="sort-btn" (click)="sortTable(sdwanSort, 'latency')">Latency <span class="sort-mark" aria-hidden="true">{{ sdwanSort.mark('latency') }}</span></button></th>
              <th class="num" [attr.aria-sort]="sdwanSort.ariaSort('jitter')">
                <button type="button" class="sort-btn" (click)="sortTable(sdwanSort, 'jitter')">Jitter <span class="sort-mark" aria-hidden="true">{{ sdwanSort.mark('jitter') }}</span></button></th>
              <th class="num" [attr.aria-sort]="sdwanSort.ariaSort('loss')">
                <button type="button" class="sort-btn" (click)="sortTable(sdwanSort, 'loss')">Loss <span class="sort-mark" aria-hidden="true">{{ sdwanSort.mark('loss') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let member of sdwanShown; trackBy: trackByName" [attr.data-ev]="'sdwan:' + member.name">
              <td><b>{{ member.name }}</b></td>
              <td><span class="label" [ngClass]="sdwanBadge(member).cls">{{ sdwanBadge(member).text }}</span></td>
              <td class="num" [ngClass]="sdwanCls(member.latency, 'latency')">{{ fmtMs(member.latency) }}</td>
              <td class="num" [ngClass]="sdwanCls(member.jitter, 'jitter')">{{ fmtMs(member.jitter) }}</td>
              <td class="num">{{ member.loss == null ? '-' : (member.loss | number:'1.1-1') + ' %' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="card" [ngClass]="hasSslUsers ? 'span6' : 'span12'" *ngIf="hasTunnels"
         data-ev="card:tunnels">
      <div class="card-header">IPsec tunnels
        <span class="hdr-note">{{ vpnTunnels.length }} configured</span>
      </div>
      <div class="tbl-wrap">
        <table class="clr sortable">
          <thead>
            <tr>
              <th [attr.aria-sort]="tunnelSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(tunnelSort, 'name')">Tunnel <span class="sort-mark" aria-hidden="true">{{ tunnelSort.mark('name') }}</span></button></th>
              <th [attr.aria-sort]="tunnelSort.ariaSort('state')">
                <button type="button" class="sort-btn" (click)="sortTable(tunnelSort, 'state')">State <span class="sort-mark" aria-hidden="true">{{ tunnelSort.mark('state') }}</span></button></th>
              <th class="num" [attr.aria-sort]="tunnelSort.ariaSort('now')">
                <button type="button" class="sort-btn" (click)="sortTable(tunnelSort, 'now')">Now <span class="sort-mark" aria-hidden="true">{{ tunnelSort.mark('now') }}</span></button></th>
              <th class="num" [attr.aria-sort]="tunnelSort.ariaSort('total')">
                <button type="button" class="sort-btn" (click)="sortTable(tunnelSort, 'total')">Total <span class="sort-mark" aria-hidden="true">{{ tunnelSort.mark('total') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let tunnel of tunnelsShown; trackBy: trackByName" [attr.data-ev]="'tunnel:' + tunnel.name">
              <td><b>{{ tunnel.name }}</b><span class="dim" *ngIf="tunnel.rgwy"> {{ tunnel.rgwy }}</span></td>
              <td>
                <span class="label" [ngClass]="tunnel.up ? 'label-success' : 'label-danger'">
                  {{ tunnel.up ? 'up' : 'down' }}
                </span>
              </td>
              <td class="num">{{ tunnel.bps_now | fmtBps }}</td>
              <td class="num dim">{{ tunnel.bytes | fmtBytes }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="card" [ngClass]="hasTunnels ? 'span6' : 'span12'" *ngIf="hasSslUsers"
         data-ev="card:ssl">
      <div class="card-header">SSL-VPN users over time</div>
      <div class="card-block"><div id="ch-ssl" class="chart"></div></div>
    </div>
    <div class="card span12" *ngIf="hasRouting" data-ev="card:routing">
      <div class="card-header">Routing neighbors
        <span class="hdr-note">latest poll; BGP should be Established, OSPF Full
          (2-Way is normal between two routers that are neither DR nor BDR)</span>
      </div>
      <div class="tbl-wrap">
        <!-- BGP and OSPF peers in one list (routingShown), so they sort together -->
        <table class="clr sortable">
          <thead>
            <tr>
              <th [attr.aria-sort]="routingSort.ariaSort('protocol')">
                <button type="button" class="sort-btn" (click)="sortTable(routingSort, 'protocol')">Protocol <span class="sort-mark" aria-hidden="true">{{ routingSort.mark('protocol') }}</span></button></th>
              <th [attr.aria-sort]="routingSort.ariaSort('neighbor')">
                <button type="button" class="sort-btn" (click)="sortTable(routingSort, 'neighbor')">Neighbor <span class="sort-mark" aria-hidden="true">{{ routingSort.mark('neighbor') }}</span></button></th>
              <th [attr.aria-sort]="routingSort.ariaSort('state')">
                <button type="button" class="sort-btn" (click)="sortTable(routingSort, 'state')">State <span class="sort-mark" aria-hidden="true">{{ routingSort.mark('state') }}</span></button></th>
              <th [attr.aria-sort]="routingSort.ariaSort('details')">
                <button type="button" class="sort-btn" (click)="sortTable(routingSort, 'details')">Details <span class="sort-mark" aria-hidden="true">{{ routingSort.mark('details') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let peer of routingShown; trackBy: trackByRouting" [attr.data-ev]="peer.ev">
              <td>{{ peer.protocol }}</td><td><b>{{ peer.neighbor }}</b></td>
              <td><span class="label" [ngClass]="peer.cls">{{ peer.state }}</span></td>
              <td class="dim">{{ peer.details }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ng-container>

  <!-- =========================================================== NETWORK MAP -->
  <app-fortinet-netmap *ngIf="tab === 'map'" class="span12"
                       [netmap]="netmap" [ports]="ports" [links]="ifaceRows"
                       [serial]="serial === '-' ? null : serial" [range]="histRange" [apiBase]="apiBase"
                       [savedState]="netmapState" (stateSaved)="onNetmapStateSaved($event)"
                       [loading]="!!pendingRange" [coverage]="coverageText" [coverageCut]="coverageCut"
                       [deviceTitle]="deviceTitle"
                       [model]="ports?.model ?? null"></app-fortinet-netmap>

  <!-- ============================================================ INTERFACES -->
  <app-fortinet-interfaces *ngIf="tab === 'interfaces'" class="span12"
                           [ports]="ports" [issues]="issues"
                           [fallbackTitle]="deviceTitle"></app-fortinet-interfaces>

  <!-- ================================================================ SYSTEM -->
  <ng-container *ngIf="tab === 'system'">
    <div class="card span12" data-ev="card:cpumem">
      <div class="card-header">CPU &amp; memory
        <span class="label label-danger hdr-badge" *ngIf="systemState?.conserve">conserve mode</span>
        <span class="label label-info hdr-badge" *ngIf="!systemState?.conserve && systemState?.conserve_seen"
              title="The unit was in conserve mode earlier in the window; see Events">conserve mode earlier</span>
        <span class="hdr-note">CPU on the left axis, memory on the right<span
          *ngIf="systemState?.last_reboot">; last reboot {{ fmtStamp(systemState!.last_reboot) }}</span></span>
      </div>
      <div class="card-block"><div id="ch-mem" class="chart chart-cpumem"></div></div>
    </div>

    <div class="card span4" data-ev="card:ha">
      <div class="card-header">HA cluster
        <span class="hdr-note">{{ haMembers.length ? haMembers.length + ' members' : 'standalone' }}</span>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!haMembers.length">This device is not clustered.</div>
        <table class="clr sortable" *ngIf="haMembers.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="haSort.ariaSort('member')">
                <button type="button" class="sort-btn" (click)="sortTable(haSort, 'member')">Member <span class="sort-mark" aria-hidden="true">{{ haSort.mark('member') }}</span></button></th>
              <th [attr.aria-sort]="haSort.ariaSort('role')">
                <button type="button" class="sort-btn" (click)="sortTable(haSort, 'role')">Role <span class="sort-mark" aria-hidden="true">{{ haSort.mark('role') }}</span></button></th>
              <th class="num" [attr.aria-sort]="haSort.ariaSort('cpu')">
                <button type="button" class="sort-btn" (click)="sortTable(haSort, 'cpu')">CPU <span class="sort-mark" aria-hidden="true">{{ haSort.mark('cpu') }}</span></button></th>
              <th class="num" [attr.aria-sort]="haSort.ariaSort('mem')">
                <button type="button" class="sort-btn" (click)="sortTable(haSort, 'mem')">Memory <span class="sort-mark" aria-hidden="true">{{ haSort.mark('mem') }}</span></button></th>
              <th class="num" [attr.aria-sort]="haSort.ariaSort('sessions')">
                <button type="button" class="sort-btn" (click)="sortTable(haSort, 'sessions')">Sessions <span class="sort-mark" aria-hidden="true">{{ haSort.mark('sessions') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let member of haShown; trackBy: trackByHa">
              <td><b>{{ member.hostname }}</b>
                <div class="dim small" *ngIf="member.serial">{{ member.serial }}<span
                  *ngIf="member.serial === serial">, this device</span></div></td>
              <td>
                <span class="label" [ngClass]="member.is_master ? 'label-info' : 'label-success'">
                  {{ member.is_master ? 'primary' : 'secondary' }}
                </span>
              </td>
              <td class="num" [ngClass]="pctClass(member.cpu, 'cpu')">{{ member.cpu | fmtPct }}</td>
              <td class="num" [ngClass]="pctClass(member.mem, 'memory')">{{ member.mem | fmtPct }}</td>
              <td class="num">{{ member.sessions | fmtNum }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card span4" data-ev="card:sensors">
      <div class="card-header">Sensors &amp; FortiGuard</div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!sensorRows.length && !fortiguard?.available">
          No hardware sensors or FortiGuard data (a FortiGate-VM has no sensors).</div>
        <!-- the FortiGuard line always stays at the bottom, below the sorted sensors -->
        <table class="clr sortable" *ngIf="sensorRows.length || fortiguard?.available">
          <thead>
            <tr>
              <th [attr.aria-sort]="sensorSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(sensorSort, 'name')">Sensor <span class="sort-mark" aria-hidden="true">{{ sensorSort.mark('name') }}</span></button></th>
              <th class="num" [attr.aria-sort]="sensorSort.ariaSort('state')">
                <button type="button" class="sort-btn" (click)="sortTable(sensorSort, 'state')">State <span class="sort-mark" aria-hidden="true">{{ sensorSort.mark('state') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let sensor of sensorShown; trackBy: trackByName" [attr.data-ev]="'sensor:' + sensor.name">
              <td><b>{{ sensor.name }}</b><span class="dim"> {{ sensor.value }}</span></td>
              <td class="num">
                <span class="label" [ngClass]="sensor.alarm ? 'label-danger' : 'label-success'">
                  {{ sensor.alarm ? 'alarm' : 'ok' }}
                </span>
              </td>
            </tr>
            <tr *ngIf="fortiguard?.available">
              <td><b>FortiGuard</b><span class="dim"> update servers</span></td>
              <td class="num">
                <span class="label"
                      [ngClass]="fortiguard!.connected > 0 ? 'label-success' : 'label-warning'">
                  {{ fortiguard!.connected }} of {{ fortiguard!.servers }} up
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card span4">
      <div class="card-header">Admins logged in
        <span class="hdr-note">{{ adminRows.length }}</span>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!adminRows.length">No admin sessions.</div>
        <table class="clr sortable" *ngIf="adminRows.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="adminSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(adminSort, 'name')">Admin <span class="sort-mark" aria-hidden="true">{{ adminSort.mark('name') }}</span></button></th>
              <th [attr.aria-sort]="adminSort.ariaSort('from')">
                <button type="button" class="sort-btn" (click)="sortTable(adminSort, 'from')">From <span class="sort-mark" aria-hidden="true">{{ adminSort.mark('from') }}</span></button></th>
              <th class="num" [attr.aria-sort]="adminSort.ariaSort('since')">
                <button type="button" class="sort-btn" (click)="sortTable(adminSort, 'since')">Since <span class="sort-mark" aria-hidden="true">{{ adminSort.mark('since') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let admin of adminShown; trackBy: trackByAdmin">
              <td><b>{{ admin.name }}</b><span class="dim" *ngIf="admin.method"> {{ admin.method }}</span></td>
              <td class="dim">{{ admin.ip || '-' }}</td>
              <td class="num dim" [title]="loginTitle(admin.login_time)">{{ loginAge(admin.login_time) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card span6" data-ev="card:time" *ngIf="systemState?.clock_skew != null || ntp?.available">
      <div class="card-header">Clock &amp; NTP
        <span class="hdr-note">skew against the collector's clock</span>
      </div>
      <div class="fw-strip" *ngIf="systemState?.clock_skew != null">
        <div>
          <span class="fw-label">Device clock</span>
          <span class="fw-version" [ngClass]="skewCls(systemState!.clock_skew)">{{ skewText(systemState!.clock_skew) }}</span>
        </div>
        <span class="dim small" [title]="'Measured on the poll of ' + fmtStamp(systemState!.clock_at)">
          includes the collector host's own error</span>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="ntp?.available && !ntp!.servers.length">No NTP server reported.</div>
        <table class="clr sortable" *ngIf="ntp?.servers?.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="ntpSort.ariaSort('server')">
                <button type="button" class="sort-btn" (click)="sortTable(ntpSort, 'server')">NTP server <span class="sort-mark" aria-hidden="true">{{ ntpSort.mark('server') }}</span></button></th>
              <th [attr.aria-sort]="ntpSort.ariaSort('state')">
                <button type="button" class="sort-btn" (click)="sortTable(ntpSort, 'state')">State <span class="sort-mark" aria-hidden="true">{{ ntpSort.mark('state') }}</span></button></th>
              <th class="num" [attr.aria-sort]="ntpSort.ariaSort('stratum')">
                <button type="button" class="sort-btn" (click)="sortTable(ntpSort, 'stratum')">Stratum <span class="sort-mark" aria-hidden="true">{{ ntpSort.mark('stratum') }}</span></button></th>
              <th class="num" [attr.aria-sort]="ntpSort.ariaSort('offset')">
                <button type="button" class="sort-btn" (click)="sortTable(ntpSort, 'offset')">Offset <span class="sort-mark" aria-hidden="true">{{ ntpSort.mark('offset') }}</span></button></th>
              <th class="num" [attr.aria-sort]="ntpSort.ariaSort('delay')">
                <button type="button" class="sort-btn" (click)="sortTable(ntpSort, 'delay')">Delay <span class="sort-mark" aria-hidden="true">{{ ntpSort.mark('delay') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let server of ntpShown; trackBy: trackByServer">
              <td><b>{{ server.server }}</b><span class="dim" *ngIf="server.ip && server.ip !== server.server"> {{ server.ip }}</span></td>
              <td><span class="label" [ngClass]="server.reachable ? 'label-success' : 'label-danger'">{{ server.reachable ? 'reachable' : 'unreachable' }}</span>
                <span class="dim small" *ngIf="server.selected"> selected</span></td>
              <td class="num dim">{{ server.stratum ?? '-' }}</td>
              <td class="num">{{ fmtMs(server.offset_ms) }}</td>
              <td class="num dim">{{ fmtMs(server.delay_ms) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card span6" data-ev="card:certs" *ngIf="certRows.length">
      <div class="card-header">Certificates
        <span class="hdr-note" *ngIf="certSort.key === 'expires' && certSort.ascending">soonest expiry first</span>
      </div>
      <div class="tbl-wrap">
        <div class="empty" *ngIf="!certsShown.length">Only factory certificates.</div>
        <table class="clr license-tbl sortable" *ngIf="certsShown.length">
          <thead>
            <tr>
              <th [attr.aria-sort]="certSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(certSort, 'name')">Certificate <span class="sort-mark" aria-hidden="true">{{ certSort.mark('name') }}</span></button></th>
              <th class="num" [attr.aria-sort]="certSort.ariaSort('expires')">
                <button type="button" class="sort-btn" (click)="sortTable(certSort, 'expires')">Expires <span class="sort-mark" aria-hidden="true">{{ certSort.mark('expires') }}</span></button></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let cert of certsShown; trackBy: trackByName" [attr.data-ev]="'cert:' + cert.name"
                [title]="certTitle(cert)">
              <td><b>{{ cert.name }}</b><span class="dim" *ngIf="cert.subject"> {{ cert.subject }}</span>
                <span class="label label-info" *ngIf="cert.built_in">factory</span></td>
              <td class="num"><span class="label" [ngClass]="certBadge(cert).cls">{{ certBadge(cert).text }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="lic-more" *ngIf="certRows.length > certsShown.length || showBuiltinCerts">
        <button type="button" class="link-btn" [attr.aria-expanded]="showBuiltinCerts"
                (click)="toggleBuiltinCerts()">
          {{ showBuiltinCerts ? '▾ Hide the factory certificates' : '▸ Show the factory certificates too' }}
        </button>
      </div>
    </div>

    <!-- Endpoint polls: what every endpoint answered on the last polls, from
         the poll index fortinet_<serial>_poll.json (document poll_report,
         built by build_poll_report in dashboard.py). The explanations come
         from `explain` in endpoints.py, or from HTTP_STATUS_HELP in
         dashboard.py. A row has data-ev="poll:<suffix>" so the "Show" link
         of a poll-failing / poll-absent issue can highlight it.
         The card is always shown: when the document has no poll_report at
         all, it says that the API still runs an older dashboard.py. -->
    <div class="card span12" data-ev="card:polls">
      <div class="card-header has-filter">
        <span class="card-title">Endpoint polls</span>
        <div class="btn-group poll-filter" role="group" aria-label="Show the endpoints"
             *ngIf="pollReport?.available">
          <button *ngFor="let option of pollFilters" type="button"
                  [class.active]="pollFilter === option.id" [attr.aria-pressed]="pollFilter === option.id"
                  (click)="setPollFilter(option.id)">
            {{ option.label }} <span class="poll-count">{{ pollFilterCounts[option.id] }}</span>
          </button>
        </div>
        <span class="hdr-note push-right" *ngIf="pollReport?.available">
          {{ pollSummary }}; last {{ pollReport!.cycles }} polls, the newest {{ fmtStamp(pollReport!.last_poll) }}
        </span>
      </div>

      <div class="poll-notes" *ngIf="pollReport?.device_notes?.length">
        <p *ngFor="let note of pollReport!.device_notes">{{ note }}</p>
      </div>

      <div class="tbl-wrap poll-tbl-wrap">
        <div class="empty" *ngIf="!pollReport">
          The server did not send this card's data: the NetScope API runs an older version than
          this page. To fix it, see the technical guide, section "Specific cards" of
          "Troubleshooting".</div>
        <div class="empty" *ngIf="pollReport && !pollReport.available">
          No poll index yet (fortinet_{{ serial }}_poll.json). It appears after the collector's first
          complete cycle on this device.</div>
        <div class="empty" *ngIf="pollReport?.available && !pollShown.length">No endpoint in this group.</div>
        <table class="clr sortable poll-tbl" *ngIf="pollShown.length">
          <thead>
            <tr>
              <th class="pill-col" [attr.aria-sort]="pollSort.ariaSort('status')">
                <button type="button" class="sort-btn" (click)="sortTable(pollSort, 'status')">State <span class="sort-mark" aria-hidden="true">{{ pollSort.mark('status') }}</span></button></th>
              <th [attr.aria-sort]="pollSort.ariaSort('name')">
                <button type="button" class="sort-btn" (click)="sortTable(pollSort, 'name')">Endpoint <span class="sort-mark" aria-hidden="true">{{ pollSort.mark('name') }}</span></button></th>
              <th [attr.aria-sort]="pollSort.ariaSort('last')">
                <button type="button" class="sort-btn" (click)="sortTable(pollSort, 'last')">Last answer <span class="sort-mark" aria-hidden="true">{{ pollSort.mark('last') }}</span></button></th>
              <th [attr.aria-sort]="pollSort.ariaSort('polls')">
                <button type="button" class="sort-btn" (click)="sortTable(pollSort, 'polls')">Last {{ pollReport!.cycles }} polls <span class="sort-mark" aria-hidden="true">{{ pollSort.mark('polls') }}</span></button></th>
              <th [attr.aria-sort]="pollSort.ariaSort('last_ok')">
                <button type="button" class="sort-btn" (click)="sortTable(pollSort, 'last_ok')">Last success <span class="sort-mark" aria-hidden="true">{{ pollSort.mark('last_ok') }}</span></button></th>
              <th class="plain">Why, and is it a problem?</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of pollShown; trackBy: trackByPoll" [attr.data-ev]="'poll:' + item.row.suffix">
              <td class="pill-col"><span class="label" [ngClass]="item.badge.cls">{{ item.badge.text }}</span></td>
              <td>
                <b>{{ item.row.name }}</b>
                <span class="label label-info" *ngIf="item.row.optional"
                      [title]="'Optional: ' + item.row.expected.join(', ') + ' means not on this device'">optional</span>
                <div class="dim small poll-path" *ngIf="item.row.path">GET {{ item.row.path }}</div>
                <div class="dim small">file suffix {{ item.row.suffix }}<span *ngIf="item.row.kind">, {{ item.row.kind }}</span></div>
              </td>
              <td>
                {{ item.row.last_status_text }}
                <div class="dim small" *ngIf="item.row.last_failure && item.row.verdict !== 'expected'">
                  last failure {{ fmtStamp(item.row.last_failure) }}</div>
              </td>
              <td>
                <!-- one square per poll, the oldest on the left -->
                <span class="poll-strip" role="img"
                      [attr.aria-label]="item.okText + ' over the last ' + item.marks.length + ' polls'">
                  <span *ngFor="let mark of item.marks; trackBy: trackByIndex" class="poll-mark"
                        [ngClass]="mark.cls" [title]="mark.title"></span>
                </span>
                <div class="dim small">{{ item.okText }}</div>
              </td>
              <td [class.dim]="!item.row.last_ok">
                {{ item.row.last_ok ? fmtStamp(item.row.last_ok) : (item.row.polled ? 'none in these polls' : '-') }}</td>
              <td class="poll-why">
                <span *ngIf="item.row.explanation">{{ item.row.explanation }}</span>
                <span class="dim" *ngIf="!item.row.explanation">Answering normally.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="poll-legend" *ngIf="pollShown.length">
        <span class="key"><span class="poll-mark pm-ok"></span>2xx answer</span>
        <span class="key"><span class="poll-mark pm-expected"></span>error expected on this device</span>
        <span class="key"><span class="poll-mark pm-fail"></span>error</span>
        <span class="key"><span class="poll-mark pm-none"></span>no answer</span>
        <span class="key"><span class="poll-mark pm-skip"></span>not polled</span>
        <span class="dim">Hover a square for the time and the HTTP status.</span>
      </div>
    </div>
  </ng-container>
  </div>
  </ng-container>
</main>

<!-- One-page report, shown only while printing -->
<section class="report" *ngIf="reportMode && !fleetMode" aria-label="Device report">
  <div class="report-bar no-print">
    <span>Print preview. Use the print dialog to print or save as PDF.</span>
    <button type="button" class="btn" (click)="closeReport()">Close preview</button>
  </div>
  <h1>{{ deviceTitle }}</h1>
  <p class="report-meta">Serial {{ serial }}. FortiOS {{ version }}<span
     *ngFor="let group of headerGroups; let first = first">{{ first ? '. Groups ' : ', ' }}{{ group.text }}</span>. Report of
     {{ reportAt }}, history window {{ histRange }}<span *ngIf="staleText">. {{ staleText }}</span>.</p>

  <h2>Key figures</h2>
  <table class="report-tbl">
    <tbody>
      <tr *ngFor="let kpi of kpis; trackBy: trackByKpi">
        <th>{{ kpi.label }}</th>
        <td>{{ kpi.value }}{{ kpi.suffix }}</td>
        <td>{{ kpi.badge?.text || kpi.sub }}</td>
      </tr>
    </tbody>
  </table>

  <h2>Health checks</h2>
  <table class="report-tbl">
    <ng-container *ngFor="let checkGroup of checkGroups">
      <tbody>
        <tr *ngFor="let check of checkGroup.checks; trackBy: trackByCheck">
          <th>{{ checkGroup.name }}, {{ check.label }}</th>
          <td><span class="label" [ngClass]="check.badge.cls">{{ check.badge.text }}</span></td>
          <td>{{ check.metric }}</td>
        </tr>
      </tbody>
    </ng-container>
  </table>

  <h2>Open issues ({{ issues.length }})</h2>
  <p *ngIf="!issues.length">None.</p>
  <ul class="report-list" *ngIf="issues.length">
    <li *ngFor="let issue of issues; trackBy: trackByIssue">
      <span class="label" [ngClass]="(issue | issueBadge).cls">{{ (issue | issueBadge).text }}</span>
      <b>{{ issue.title }}</b><span *ngIf="issue.detail">. {{ issue.detail }}</span>
    </li>
  </ul>
  <ng-container *ngIf="issuesAcked.length">
    <h2>Acknowledged issues ({{ issuesAcked.length }})</h2>
    <ul class="report-list">
      <li *ngFor="let issue of issuesAcked; trackBy: trackByIssue">
        <span class="label" [ngClass]="(issue | issueBadge).cls">{{ (issue | issueBadge).text }}</span>
        {{ issue.title }}. <span class="dim">{{ ackNote(issue, false) }} {{ ackNoteLine(issue) }}</span>
      </li>
    </ul>
  </ng-container>

  <div class="report-cols">
    <div *ngIf="licenseActive.length">
      <h2>Licenses</h2>
      <table class="report-tbl">
        <tbody>
          <tr *ngFor="let license of licenseActive; trackBy: trackByName">
            <th>{{ license.name }}</th>
            <td><span class="label" [ngClass]="licenseBadge(license).cls">{{ licenseBadge(license).text }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>
    <div *ngIf="vpnTunnels.length || sdwanRows.length">
      <h2>VPN &amp; WAN</h2>
      <table class="report-tbl">
        <tbody>
          <tr *ngFor="let tunnel of vpnTunnels; trackBy: trackByName">
            <th>IPsec {{ tunnel.name }}</th><td>{{ tunnel.up ? 'up' : 'down' }}</td>
          </tr>
          <tr *ngFor="let member of sdwanRows; trackBy: trackByName">
            <th>SD-WAN {{ member.name }}</th><td>{{ sdwanBadge(member).text }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="report-cols" *ngIf="systemState?.available || ntp?.available || hasRouting">
    <div *ngIf="systemState?.available || ntp?.available">
      <h2>Time and memory</h2>
      <table class="report-tbl">
        <tbody>
          <tr *ngIf="systemState?.clock_skew != null"><th>Clock</th><td>{{ skewText(systemState!.clock_skew) }}</td></tr>
          <tr *ngIf="ntp?.available"><th>NTP</th><td>{{ ntp!.reachable }} of {{ ntp!.servers.length }} reachable</td></tr>
          <tr *ngIf="systemState?.conserve != null"><th>Conserve mode</th>
            <td>{{ systemState!.conserve ? 'yes' : systemState!.conserve_seen ? 'earlier in the window' : 'no' }}</td></tr>
          <tr *ngIf="systemState?.last_reboot"><th>Last reboot</th><td>{{ fmtStamp(systemState!.last_reboot) }}</td></tr>
        </tbody>
      </table>
    </div>
    <div *ngIf="hasRouting">
      <h2>Routing neighbors</h2>
      <table class="report-tbl">
        <tbody>
          <tr *ngFor="let peer of bgpRows; trackBy: trackByPeer"><th>BGP {{ peer.neighbor }}</th><td>{{ peer.state }}</td></tr>
          <tr *ngFor="let peer of ospfRows; trackBy: trackByPeer"><th>OSPF {{ peer.neighbor }}</th><td>{{ peer.state }}</td></tr>
        </tbody>
      </table>
    </div>
  </div>

  <ng-container *ngIf="certsShown.length">
    <h2>Certificates</h2>
    <table class="report-tbl">
      <tbody>
        <tr *ngFor="let cert of certsShown; trackBy: trackByName">
          <th>{{ cert.name }}</th>
          <td><span class="label" [ngClass]="certBadge(cert).cls">{{ certBadge(cert).text }}</span></td>
        </tr>
      </tbody>
    </table>
  </ng-container>

  <ng-container *ngIf="events.length">
    <h2>Recent events ({{ events.length > 15 ? '15 of ' + eventsTotal : events.length }})</h2>
    <ul class="report-list">
      <li *ngFor="let event of events.slice(0, 15); trackBy: trackByEvent">
        <span class="label" [ngClass]="eventBadge(event.sev).cls">{{ eventBadge(event.sev).text }}</span>
        {{ fmtStamp(event.t) }} {{ event.title }}
      </li>
    </ul>
  </ng-container>

  <!-- only the busiest interfaces, so the report stays on one page -->
  <h2>{{ reportIfaceTitle }}</h2>
  <table class="report-tbl report-grid">
    <thead>
      <tr><th>Interface</th><th>Link</th><th>RX now</th><th>TX now</th><th>Util</th><th>Errors</th></tr>
    </thead>
    <tbody>
      <tr *ngFor="let iface of reportIfaceRows; trackBy: trackByName">
        <td>{{ iface.name }} <span class="dim">{{ iface.alias }}</span></td>
        <td>{{ iface.link ? 'up' : 'down' }}</td>
        <td>{{ iface.rx_bps | fmtBps }}</td>
        <td>{{ iface.tx_bps | fmtBps }}</td>
        <td>{{ iface.util == null ? '-' : iface.util + '%' }}</td>
        <td>{{ iface.rx_errors + iface.tx_errors | fmtNum }}</td>
      </tr>
    </tbody>
  </table>
</section>

<!-- The groups of the device, shown in both headers (the component's own one and
     the page toolbar). The main group is in bold. A click opens the "All devices"
     page filtered on that group. -->
<ng-template #groupTags>
  <span class="tag group-tags" *ngIf="!fleetMode && headerGroups.length">
    {{ headerGroups.length > 1 ? 'Groups' : 'Group' }}
    <button type="button" class="group-link" *ngFor="let group of headerGroups; trackBy: trackByName"
            [class.main-group]="group.main"
            [title]="(group.main ? 'Main group. ' : '') + 'Show the devices of ' + group.text"
            [attr.aria-label]="(group.main ? 'Main group. ' : '') + 'Show the devices of ' + group.text"
            (click)="openGroupOnFleet(group.name)">{{ group.text }}</button>
  </span>
</ng-template>
